import type {
  ApiEnvelope,
  BrandResponse,
  CacheResponse,
  CircuitsResponse,
  ErrorEnvelope,
  ErrorsResponse,
  FetchRequestBody,
  FetchResponseBody,
  OverviewResponse,
  TimeWindow,
  AuthResponse,
  BrandListResponse,
  BrandRecord,
} from "./types";

/**
 * Resolves the Central-API base URL.
 *
 * - In dev, leave VITE_API_BASE unset and let the Vite proxy handle `/api`.
 * - In a built/preview deployment, set VITE_API_BASE=https://your-host so all
 *   requests go straight there (e.g. `https://central-api.acme.io`).
 */
const RAW_BASE = import.meta.env.VITE_API_BASE ?? "";
export const API_BASE = RAW_BASE.replace(/\/+$/, "");

class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const buildUrl = (path: string, params?: Record<string, string | number | undefined>) => {
  const url = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
};

const request = async <T,>(
  path: string,
  init: RequestInit = {},
  params?: Record<string, string | number | undefined>,
): Promise<T> => {
  const token = localStorage.getItem("dashboard.token");
  const headers = new Headers(init.headers || {});
  headers.set("content-type", "application/json");
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  }

  const res = await fetch(buildUrl(path, params), {
    ...init,
    headers,
  });

  // Try to parse JSON regardless of status — Central-API always returns JSON.
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON */
  }

  if (!res.ok) {
    const err = body as Partial<ErrorEnvelope> | null;
    throw new ApiError(
      res.status,
      err?.message || `Request failed with HTTP ${res.status}`,
      err?.code,
      err?.details,
    );
  }

  return body as T;
};

const unwrap = <T,>(envelope: ApiEnvelope<T>): T => envelope.response;

// ── Stats endpoints (no auth required) ─────────────────────────────

export const fetchOverview = (window: TimeWindow) =>
  request<ApiEnvelope<OverviewResponse>>(
    "/api/v1/stats/overview",
    {},
    { since: window },
  ).then(unwrap);

export const fetchCacheInsights = () =>
  request<ApiEnvelope<CacheResponse>>("/api/v1/stats/cache").then(unwrap);

export const fetchErrors = (window: TimeWindow) =>
  request<ApiEnvelope<ErrorsResponse>>(
    "/api/v1/stats/errors",
    {},
    { since: window },
  ).then(unwrap);

export const fetchBrand = (brandId: string, window: TimeWindow) =>
  request<ApiEnvelope<BrandResponse>>(
    `/api/v1/stats/brand/${encodeURIComponent(brandId)}`,
    {},
    { since: window },
  ).then(unwrap);

export const fetchCircuits = () =>
  request<ApiEnvelope<CircuitsResponse>>("/api/v1/stats/circuits").then(unwrap);

// ── Tester: hit the unified /fetch endpoint with brand creds ───────

export interface FetchTestResult {
  status: number;
  envelope: ApiEnvelope<FetchResponseBody> | ErrorEnvelope;
}

export const callFetchEndpoint = async (
  body: FetchRequestBody,
  creds: { brand: string; apiKey: string },
): Promise<FetchTestResult> => {
  const res = await fetch(buildUrl("/api/v1/fetch"), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-brand": creds.brand,
      "x-api-key": creds.apiKey,
    },
    body: JSON.stringify(body),
  });
  let envelope: ApiEnvelope<FetchResponseBody> | ErrorEnvelope;
  try {
    envelope = (await res.json()) as
      | ApiEnvelope<FetchResponseBody>
      | ErrorEnvelope;
  } catch {
    envelope = {
      status_code: res.status,
      success: false,
      code: "PARSE_ERROR",
      message: `Server returned non-JSON ${res.status}`,
      cache_hit: false,
      data_source: null,
    };
  }
  return { status: res.status, envelope };
};

// ── Auth & Brand Keys ───────────────────────────────────────────

export const login = (email: string, password: string) =>
  request<ApiEnvelope<AuthResponse>>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  }).then(unwrap);

export const fetchBrandKeys = () =>
  request<ApiEnvelope<BrandListResponse>>("/api/v1/auth/brands").then(unwrap);

export const updateBrandStatus = (id: string, status: "ACTIVE" | "DISABLED") =>
  request<ApiEnvelope<{ brand: any }>>(`/api/v1/auth/brands/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  }).then(unwrap);

export const createBrand = (brandData: {
  brand_id: string;
  brand_name: string;
  api_key?: string;
  rate_limit_per_min?: number;
  rate_limit_per_day?: number;
}) =>
  request<ApiEnvelope<{ brand: BrandRecord }>>("/api/v1/auth/brands", {
    method: "POST",
    body: JSON.stringify(brandData),
  }).then(unwrap);

export { ApiError };
